'use client';

import { ChatSkillsModal } from '../ChatSkillsModal';
import { ChatStartScreenCompact } from './start-screen/ChatStartScreenCompact';
import { ChatStartScreenFull } from './start-screen/ChatStartScreenFull';
import { useStartScreenComposer } from './start-screen/useStartScreenComposer';
import type { StartChatOptions } from './start-screen/useStartScreenComposer';

interface ChatStartScreenProps {
  userContext: { name?: string; companyName?: string } | null | undefined;
  locale: string;
  isLoading: boolean;
  onStartChat: (query: string, options: StartChatOptions) => void;
  /**
   * Extension-only compact variant for Chrome sidepanel/popup surfaces.
   * This is intentionally not tied to phone/mobile breakpoints.
   */
  compact?: boolean;
  meridianLogoUrl?: string;
  /** Render the Slack availability callout in the empty start screen (default true). */
  showSlackCallout?: boolean;
  /** Also advertise Meridian's Slack SEARCH capability in the callout (default false). */
  slackCalloutMentionsSearch?: boolean;
  /** MCP connections page — surfaced in the composer's "+" menu (full variant). */
  mcpConnectionsHref?: string;
  /** Meridian API base — uploads, skills, and the model catalog resolve from it. */
  apiBaseUrl?: string;
  /** Inline model switcher in the composer (default true). */
  showModelPicker?: boolean;
}

/**
 * Hero section shown when no chat is selected. Delegates to the compact or full
 * variant; both share a single composer (Enter-to-send with IME safety).
 *
 * The full variant renders the SAME composer as an active conversation, so the
 * skills modal is mounted here too — "Use a skill" must work before the first
 * message exists, not only afterwards.
 */
export function ChatStartScreen({
  userContext,
  locale,
  isLoading,
  onStartChat,
  compact = false,
  meridianLogoUrl = '/meridian.png',
  showSlackCallout = true,
  slackCalloutMentionsSearch = false,
  mcpConnectionsHref,
  apiBaseUrl,
  showModelPicker = true,
}: ChatStartScreenProps) {
  const composer = useStartScreenComposer(onStartChat);

  if (compact) {
    return (
      <ChatStartScreenCompact
        locale={locale}
        isLoading={isLoading}
        composer={composer}
        showSlackCallout={showSlackCallout}
        slackCalloutMentionsSearch={slackCalloutMentionsSearch}
      />
    );
  }

  return (
    <>
      <ChatStartScreenFull
        userContext={userContext}
        locale={locale}
        isLoading={isLoading}
        composer={composer}
        meridianLogoUrl={meridianLogoUrl}
        showSlackCallout={showSlackCallout}
        slackCalloutMentionsSearch={slackCalloutMentionsSearch}
        mcpConnectionsHref={mcpConnectionsHref}
        apiBaseUrl={apiBaseUrl}
        showModelPicker={showModelPicker}
      />
      <ChatSkillsModal apiBaseUrl={apiBaseUrl} onSelectSkill={composer.attachSkill} />
    </>
  );
}
